import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  KeyIcon as Key,
  CircleNotchIcon as Spinner,
  CheckCircleIcon as CheckCircle,
} from "@phosphor-icons/react";
import { formatApiErrorDetail, useAuth } from "@/lib/auth";
import { CheckingFallback } from "./Login";
import { forgotPassword } from "@/lib/api";

export default function ForgotPassword() {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  if (user === undefined) return <CheckingFallback />;
  if (user) return <Navigate to="/workspaces" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await forgotPassword(email.trim().toLowerCase());
      setSubmitted(true);
    } catch (err) {
      // Even on backend error, we don't leak — but show the user something
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 bg-[var(--ml-bg)]"
      data-testid="forgot-password-page"
    >
      <div className="ml-card w-full max-w-md p-8 space-y-5">
        <div>
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] flex items-center gap-1.5 mb-1">
            <Key size={11} weight="bold" />
            Password reset
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Forgot your password?
          </h1>
          <p className="text-sm text-[var(--ml-text-secondary)] mt-1">
            Enter the email on your account and we&apos;ll send you a link to reset it.
          </p>
        </div>

        {submitted ? (
          <div
            className="flex items-start gap-2 text-sm text-[#0e6027] bg-[#defbe6] border border-[#a7f0ba] px-3 py-3"
            data-testid="forgot-password-success"
            role="status"
          >
            <CheckCircle size={16} weight="bold" className="mt-0.5 shrink-0" />
            <div>
              If an account exists for <strong>{email}</strong>, we&apos;ve sent a password-reset link. Check your inbox — the link is valid for 15 minutes.
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-5" data-testid="forgot-password-form">
            <label className="block">
              <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1">
                Email
              </div>
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="forgot-password-email"
                className="w-full px-3 py-2 border border-[var(--ml-border)] focus:outline-none focus:border-[var(--ml-primary)] text-sm"
              />
            </label>
            {error && (
              <div
                className="text-sm text-[#a2191f] bg-[#fff1f1] border border-[#ffd7d9] px-3 py-2"
                data-testid="forgot-password-error"
                role="alert"
              >
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={submitting || !email}
              data-testid="forgot-password-submit"
              className="w-full px-4 py-2 bg-[var(--ml-primary)] text-white font-semibold hover:bg-[var(--ml-primary-hover)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {submitting ? <Spinner size={16} className="animate-spin" /> : null}
              {submitting ? "Sending…" : "Send reset link"}
            </button>
          </form>
        )}

        <div className="text-sm text-[var(--ml-text-secondary)] text-center">
          <Link to="/login" className="text-[var(--ml-primary)] hover:underline font-semibold" data-testid="forgot-back-to-login">
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
